(() => {
  const SNAP_SELECTOR = '.bm-video-story--snap';
  const VIEWPORT_SELECTOR = '.bm-video-story--viewport';
  const SNAP_THRESHOLD = 0.5;
  const MOBILE_QUERY = '(max-width: 767px)';
  const TOUCH_SCROLL_IDLE_MS = 140;
  const SNAP_COOLDOWN_MS = 1000;
  const MOBILE_NAV_SELECTOR = '.bls__mobile-navigation-bar';
  const MOBILE_NAV_SAFE_GAP = 18;

  if (window.__bmVideoStorySnapController) {
    window.__bmVideoStorySnapController.refresh();
    return;
  }

  if (!('IntersectionObserver' in window)) {
    window.__bmVideoStorySnapController = { refresh() {} };
    return;
  }

  const sectionStates = new Map();
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileQuery = window.matchMedia(MOBILE_QUERY);
  let lastScrollY = Math.max(0, window.scrollY || document.documentElement.scrollTop || 0);
  let scrollDirection = 0;
  let lastScrollAt = 0;
  let snapCooldownUntil = 0;
  let touchSequenceActive = false;
  let touchActive = false;
  let touchIdleTimer = 0;
  let navClearanceFrame = 0;

  const getScrollY = () => Math.max(0, window.scrollY || document.documentElement.scrollTop || 0);

  const getVisibleRatio = (section) => {
    const rect = section.getBoundingClientRect();
    const viewportHeight = window.visualViewport?.height || window.innerHeight || document.documentElement.clientHeight;
    const viewportTop = window.visualViewport?.offsetTop || 0;
    const viewportBottom = viewportTop + viewportHeight;
    const visible = Math.max(0, Math.min(rect.bottom, viewportBottom) - Math.max(rect.top, viewportTop));
    return rect.height > 0 ? visible / rect.height : 0;
  };

  const getVisualViewportMetrics = () => ({
    height: window.visualViewport?.height || window.innerHeight || document.documentElement.clientHeight,
    top: window.visualViewport?.offsetTop || 0
  });

  const clearMobileNavClearance = (section) => {
    section.style.removeProperty('--bm-video-nav-clearance');
  };

  const updateMobileNavClearance = () => {
    navClearanceFrame = 0;
    const sections = document.querySelectorAll('.bm-video-story--mobile-v-bottom');
    if (!sections.length) return;

    if (!mobileQuery.matches) {
      sections.forEach(clearMobileNavClearance);
      return;
    }

    const nav = document.querySelector(MOBILE_NAV_SELECTOR);
    if (!nav) {
      sections.forEach(clearMobileNavClearance);
      return;
    }

    const metrics = getVisualViewportMetrics();
    const viewportTop = metrics.top;
    const viewportBottom = viewportTop + metrics.height;
    const navRect = nav.getBoundingClientRect();
    const navStyle = getComputedStyle(nav);
    const navVisible = navRect.height > 1
      && navRect.top < viewportBottom - 1
      && navRect.bottom > viewportTop + 1
      && navStyle.display !== 'none'
      && navStyle.visibility !== 'hidden'
      && Number.parseFloat(navStyle.opacity || '1') > 0.01;

    sections.forEach((section) => {
      const sectionRect = section.getBoundingClientRect();
      const onScreen = sectionRect.bottom > viewportTop && sectionRect.top < viewportBottom;
      const content = section.querySelector('.bm-video-story__content');
      const box = section.querySelector('.bm-video-story__box');

      if (!navVisible || !onScreen || !content || !box) {
        clearMobileNavClearance(section);
        return;
      }

      const current = Number.parseFloat(section.style.getPropertyValue('--bm-video-nav-clearance')) || 0;
      const boxRect = box.getBoundingClientRect();
      // boxRect already includes the current clearance. Add it back to recover
      // the uncorrected copy position, then move only by the amount genuinely
      // needed to keep a small visual gap above the rendered mobile nav.
      const uncorrectedBottom = boxRect.bottom + current;
      const required = Math.max(0, Math.ceil(uncorrectedBottom + MOBILE_NAV_SAFE_GAP - navRect.top));
      const capped = Math.min(required, Math.round(metrics.height * 0.38));

      if (Math.abs(capped - current) < 0.5) return;
      if (capped > 0) section.style.setProperty('--bm-video-nav-clearance', `${capped}px`);
      else clearMobileNavClearance(section);
    });
  };

  const scheduleMobileNavClearance = () => {
    if (navClearanceFrame) return;
    navClearanceFrame = requestAnimationFrame(updateMobileNavClearance);
  };

  const getEffectiveViewportHeight = (section, isMobile = mobileQuery.matches) => {
    const requested = isMobile
      ? section.dataset.bmMobileSectionHeight
      : section.dataset.bmDesktopSectionHeight;

    if (requested && requested !== 'auto') {
      const numeric = Number(requested);
      if (Number.isFinite(numeric)) return numeric;
    }

    if (isMobile) {
      const legacyMobileHeight = Number(section.dataset.bmMobileViewportHeight || 100);
      return Number.isFinite(legacyMobileHeight) ? legacyMobileHeight : 100;
    }

    return 100;
  };

  const applyViewportOverscan = (section) => {
    const frame = section.querySelector('.bm-video-story__frame');
    if (!frame) return;

    const effectiveHeight = getEffectiveViewportHeight(section);

    if (!mobileQuery.matches) {
      // Desktop should follow the viewport Safari is actually painting, not a
      // CSS viewport unit that may round to a slightly different used value.
      // Keep a small 2 px safety bleed on each edge without changing the
      // section's layout footprint.
      if (effectiveHeight === 100) {
        const viewportHeight = Math.ceil(getVisualViewportMetrics().height);
        frame.style.height = `${viewportHeight + 4}px`;
        frame.style.top = '-2px';
        frame.style.marginBottom = '-4px';
      } else {
        frame.style.removeProperty('height');
        frame.style.removeProperty('top');
        frame.style.removeProperty('margin-bottom');
      }
      return;
    }

    if (effectiveHeight === 100) {
      // Mobile 100% keeps the existing 2 px overscan on both edges.
      frame.style.height = 'calc(100lvh + 4px)';
      frame.style.top = '-2px';
      frame.style.marginBottom = '-4px';
      return;
    }

    frame.style.removeProperty('height');
    frame.style.removeProperty('top');
    frame.style.removeProperty('margin-bottom');
  };

  const getLayoutDocumentTop = (element) => {
    let top = 0;
    let node = element;

    // offsetTop/offsetParent belongs to the layout viewport and therefore does
    // not move when iOS Safari expands or collapses its browser chrome. This is
    // the canonical page coordinate used for 100% mobile snapping.
    while (node) {
      top += node.offsetTop || 0;
      node = node.offsetParent;
    }

    return top;
  };

  const getSnapTarget = (section) => {
    const scrollY = getScrollY();
    const rect = section.getBoundingClientRect();
    const sectionTop = scrollY + rect.top;

    if (!mobileQuery.matches) {
      const frame = section.querySelector('.bm-video-story__frame');
      if (!frame) return sectionTop;

      // Align the rendered frame itself, including its -2 px bleed, against
      // the actual visual viewport. This avoids fractional section/scroll
      // rounding becoming a visible hairline at either edge.
      const viewportTop = getVisualViewportMetrics().top;
      const desiredFrameTop = viewportTop - 2;
      return scrollY + frame.getBoundingClientRect().top - desiredFrameTop;
    }

    const mobileHeight = getEffectiveViewportHeight(section, true);
    if (mobileHeight !== 100) return sectionTop;

    // 100% mobile is intentionally tied to one fixed document coordinate.
    // Safari's top/bottom bars are browser UI layered over the page and must not
    // alter where the story snaps when approaching it from either direction.
    return getLayoutDocumentTop(section);
  };

  const settleDesktopViewportSnap = (section) => {
    if (!section.isConnected || mobileQuery.matches) return;

    const frame = section.querySelector('.bm-video-story__frame');
    if (!frame) return;

    const desiredFrameTop = getVisualViewportMetrics().top - 2;
    const correction = frame.getBoundingClientRect().top - desiredFrameTop;

    // Smooth scrolling can finish on a fractional CSS-pixel boundary. Normalize
    // the final landing against the rendered frame, not the section box.
    if (Math.abs(correction) > 0.25) {
      window.scrollBy({ top: correction, behavior: 'auto' });
    }
  };

  const performSnap = (section, state) => {
    const now = performance.now();
    if (!section.isConnected || now < snapCooldownUntil || getVisibleRatio(section) < SNAP_THRESHOLD) {
      state.pendingDirection = 0;
      return;
    }

    state.pendingDirection = 0;
    snapCooldownUntil = now + SNAP_COOLDOWN_MS;

    const top = getSnapTarget(section);
    window.scrollTo({
      top: Math.max(0, top),
      behavior: prefersReducedMotion.matches ? 'auto' : 'smooth'
    });

    // Do not perform any mobile post-snap correction. On iOS, Safari can change
    // visualViewport geometry after the gesture; correcting against that transient
    // state simply moves the apparent gap between the top and bottom. The fixed
    // document target above is the source of truth for mobile 100%.
    if (!mobileQuery.matches) {
      window.setTimeout(() => settleDesktopViewportSnap(section), prefersReducedMotion.matches ? 40 : 520);
    }
  };

  const finishTouchSequence = () => {
    touchIdleTimer = 0;
    if (touchActive) return;

    const sinceLastScroll = performance.now() - lastScrollAt;
    if (sinceLastScroll < TOUCH_SCROLL_IDLE_MS) {
      touchIdleTimer = window.setTimeout(finishTouchSequence, TOUCH_SCROLL_IDLE_MS - sinceLastScroll + 20);
      return;
    }

    touchSequenceActive = false;

    sectionStates.forEach((state, section) => {
      if (!state.pendingDirection) return;
      if (getVisibleRatio(section) < SNAP_THRESHOLD) {
        state.pendingDirection = 0;
        return;
      }
      performSnap(section, state);
    });
  };

  const scheduleTouchFinish = () => {
    if (touchIdleTimer) window.clearTimeout(touchIdleTimer);
    touchIdleTimer = window.setTimeout(finishTouchSequence, TOUCH_SCROLL_IDLE_MS);
  };

  const handleTouchStart = () => {
    touchSequenceActive = true;
    touchActive = true;
    if (touchIdleTimer) {
      window.clearTimeout(touchIdleTimer);
      touchIdleTimer = 0;
    }
  };

  const handleTouchEnd = (event) => {
    if (event.touches?.length) return;
    touchActive = false;
    scheduleTouchFinish();
  };

  const handleScroll = () => {
    scheduleMobileNavClearance();
    const nextScrollY = getScrollY();
    const delta = nextScrollY - lastScrollY;

    if (Math.abs(delta) > 1) {
      scrollDirection = delta > 0 ? 1 : -1;
      lastScrollAt = performance.now();
      lastScrollY = nextScrollY;
      if (touchSequenceActive && !touchActive) scheduleTouchFinish();
    }
  };

  const snapObserver = new IntersectionObserver((entries) => {
    const now = performance.now();

    entries.forEach((entry) => {
      const state = sectionStates.get(entry.target);
      if (!state) return;

      const previousRatio = state.ratio;
      state.ratio = entry.intersectionRatio;

      if (entry.intersectionRatio < SNAP_THRESHOLD) {
        state.pendingDirection = 0;
        return;
      }

      // React only when entering the >= 50% visible zone. Leaving the video
      // never pulls the user back, so scrolling onwards to the footer is free.
      if (previousRatio >= SNAP_THRESHOLD) return;

      // Ignore observer changes caused by resize/browser chrome rather than
      // by a recent user scroll.
      if (now - lastScrollAt > 350 || now < snapCooldownUntil) return;

      const rect = entry.boundingClientRect;
      const approachingFromAbove = scrollDirection > 0 && rect.top > 1;
      const approachingFromBelow = scrollDirection < 0 && rect.top < -1;
      if (!approachingFromAbove && !approachingFromBelow) return;

      state.pendingDirection = scrollDirection;

      // On touch devices, never start a programmatic smooth scroll while the
      // finger is down or while iOS momentum scrolling is still settling. That
      // is what caused the post-snap jump in 5.35.
      if (touchSequenceActive) {
        if (!touchActive) scheduleTouchFinish();
        return;
      }

      performSnap(entry.target, state);
    });
  }, {
    threshold: [0, SNAP_THRESHOLD, 1]
  });

  const refresh = () => {
    document.querySelectorAll(VIEWPORT_SELECTOR).forEach(applyViewportOverscan);

    const activeSections = new Set(document.querySelectorAll(SNAP_SELECTOR));

    activeSections.forEach((section) => {
      if (sectionStates.has(section)) return;
      sectionStates.set(section, { ratio: getVisibleRatio(section), pendingDirection: 0 });
      snapObserver.observe(section);
    });

    sectionStates.forEach((_state, section) => {
      if (activeSections.has(section)) return;
      snapObserver.unobserve(section);
      sectionStates.delete(section);
    });

    scheduleMobileNavClearance();
  };

  mobileQuery.addEventListener?.('change', refresh);
  window.addEventListener('resize', () => requestAnimationFrame(refresh), { passive: true });
  window.visualViewport?.addEventListener('resize', () => requestAnimationFrame(refresh), { passive: true });
  window.visualViewport?.addEventListener('scroll', scheduleMobileNavClearance, { passive: true });
  window.addEventListener('pageshow', () => requestAnimationFrame(refresh), { passive: true });
  window.addEventListener('scroll', handleScroll, { passive: true });
  window.addEventListener('touchstart', handleTouchStart, { passive: true });
  window.addEventListener('touchend', handleTouchEnd, { passive: true });
  window.addEventListener('touchcancel', handleTouchEnd, { passive: true });
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:unload', () => requestAnimationFrame(refresh));

  window.__bmVideoStorySnapController = { refresh };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh, { once: true });
  } else {
    refresh();
  }
})();

if (!customElements.get('bm-video-story-media')) {
  class BranaVideoStoryMedia extends HTMLElement {
    connectedCallback() {
      this.mediaQuery = window.matchMedia('(max-width: 767px)');
      this.handleBreakpointChange = this.render.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handlePageShow = this.handlePageShow.bind(this);
      this.mediaQuery.addEventListener?.('change', this.handleBreakpointChange);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      window.addEventListener('pageshow', this.handlePageShow);
      window.__bmVideoStorySnapController?.refresh();
      this.render();
    }

    disconnectedCallback() {
      this.mediaQuery?.removeEventListener?.('change', this.handleBreakpointChange);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      window.removeEventListener('pageshow', this.handlePageShow);
      this.videoObserver?.disconnect();
      requestAnimationFrame(() => window.__bmVideoStorySnapController?.refresh());
    }

    get autoplayEnabled() {
      return this.dataset.autoplay === 'true';
    }

    get mutedEnabled() {
      return this.dataset.muted === 'true';
    }

    get loopEnabled() {
      return this.dataset.loop === 'true';
    }

    configureVideo(video) {
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.loop = this.loopEnabled;

      if (this.mutedEnabled) {
        video.muted = true;
        video.defaultMuted = true;
        video.setAttribute('muted', '');
      }

      if (this.autoplayEnabled) {
        video.autoplay = true;
        video.setAttribute('autoplay', '');
        video.preload = 'auto';
      }
    }

    tryPlay(video = this.currentVideo) {
      if (!video || !this.autoplayEnabled || document.hidden) return;
      const playAttempt = video.play();
      if (playAttempt?.catch) playAttempt.catch(() => {});
    }

    observePlayback(video) {
      this.videoObserver?.disconnect();
      if (!this.autoplayEnabled || !('IntersectionObserver' in window)) {
        this.tryPlay(video);
        return;
      }

      this.videoObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting) {
          this.tryPlay(video);
        } else if (!video.paused) {
          video.pause();
        }
      }, { rootMargin: '20% 0px', threshold: 0.01 });

      this.videoObserver.observe(this);
    }

    handleVisibilityChange() {
      if (!document.hidden) this.tryPlay();
    }

    handlePageShow() {
      this.tryPlay();
    }

    render() {
      const slot = this.querySelector('[data-bm-video-story-slot]');
      const desktopTemplate = this.querySelector('template[data-bm-video-story-desktop]');
      const mobileTemplate = this.querySelector('template[data-bm-video-story-mobile]');
      if (!slot || (!desktopTemplate && !mobileTemplate)) return;

      const useMobile = this.mediaQuery?.matches && mobileTemplate;
      const template = useMobile ? mobileTemplate : (desktopTemplate || mobileTemplate);
      const variant = template === mobileTemplate ? 'mobile' : 'desktop';
      if (slot.dataset.bmVideoVariant === variant) {
        this.tryPlay();
        return;
      }

      const previousVideo = slot.querySelector('video');
      const previousTime = previousVideo?.currentTime || 0;
      this.videoObserver?.disconnect();

      slot.replaceChildren(template.content.cloneNode(true));
      slot.dataset.bmVideoVariant = variant;

      const video = slot.querySelector('video');
      this.currentVideo = video;
      if (!video) return;

      this.configureVideo(video);

      const resume = () => {
        if (previousTime > 0 && Number.isFinite(video.duration)) {
          video.currentTime = Math.min(previousTime, Math.max(0, video.duration - 0.1));
        }
        this.tryPlay(video);
      };

      video.addEventListener('loadedmetadata', resume, { once: true });
      video.addEventListener('loadeddata', () => this.tryPlay(video), { once: true });
      video.addEventListener('canplay', () => this.tryPlay(video), { once: true });

      this.observePlayback(video);
      video.load();
      if (video.readyState >= 1) resume();
    }
  }

  customElements.define('bm-video-story-media', BranaVideoStoryMedia);
}
